/* eslint-disable indent */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { Event } from '@material-ui/icons';
import clsx from 'clsx';
import DatePicker from 'components/DatePicker';
import ToggleCard from 'components/ToggleCard';
import { format, isValid, set } from 'date-fns';
import { Formik } from 'formik';
import i18n from 'i18n';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { EnvironmentBehaviorFieldsProps } from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { applyI18n, parseHour, controlMaxAndMinValues } from 'utils/helpers';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import screenFields from './screenFields';
import styles from './styles';

const useStyles = makeStyles(styles);

interface PeriodCardProps {
  companySelected: CompanyData | null;
  id: string;
  initialValues: EnvironmentBehaviorFieldsProps;
  mode: string;
  role?: RolesEnum;
  title: string;
  setBehavior: React.Dispatch<
    React.SetStateAction<EnvironmentBehaviorFieldsProps>
  >;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}
const PeriodCard = ({
  companySelected,
  id,
  initialValues,
  mode,
  role,
  title,
  setBehavior,
  setHasError,
}: PeriodCardProps) => {
  const classes = useStyles();

  const updatePeriod = (key, value) => {
    const [period, field] = key.split('.');

    if (period === 'first_period') {
      setBehavior(prev => ({
        ...prev,
        first_period: {
          ...prev.first_period,
          [field]: value,
        },
      }));
    } else {
      setBehavior(prev => ({
        ...prev,
        second_period: {
          ...prev.second_period,
          [field]: value,
        },
      }));
    }
  };

  const getInitialValues = (): EnvironmentBehaviorFieldsProps => {
    return {
      ...initialValues,
      first_period: {
        ...initialValues.first_period,
        scale_instances_by_licences_margin: initialValues.first_period.scale_instances_by_licences_margin.toString(),
        hour: initialValues.first_period.is_active
          ? parseHour(initialValues.first_period.hour)
          : set(new Date(), { hours: 7, minutes: 0 }),
      },
      second_period: {
        ...initialValues.second_period,
        scale_instances_by_licences_margin: initialValues.second_period.scale_instances_by_licences_margin.toString(),
        hour: initialValues.second_period.is_active
          ? parseHour(initialValues.second_period.hour)
          : set(new Date(), { hours: 18, minutes: 0 }),
      },
    };
  };

  return (
    <Grid item xs={6}>
      <Formik
        initialValues={getInitialValues()}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={() => {}}
        enableReinitialize
      >
        {({ errors, values, setFieldValue }) => (
          <ToggleCard
            id={id}
            title={title}
            active={
              initialValues?.first_period?.is_active ||
              initialValues?.second_period?.is_active
            }
            icon={
              <Event
                className={clsx({
                  [classes.iconStyleAdmin]: inAdminView(role, companySelected),
                  [classes.iconStyleSuperAdmin]: inSuperAdminView(
                    role,
                    companySelected
                  ),
                  [classes.iconStyleOperator]: inOperatorView(
                    role,
                    companySelected
                  ),
                })}
              />
            }
            getActive={value => {
              if (value) {
                updatePeriod('first_period.is_active', false);
                updatePeriod('second_period.is_active', false);
              }
            }}
          >
            <Typography className={classes.titleCard}>
              {i18n.t('DEFINE_PERIODS_SUBTITLE_ONE_KEY')}
            </Typography>

            <Grid
              container
              spacing={2}
              xs={10}
              className={classes.marginContentCard}
              justify="space-between"
            >
              <Grid container item xs={6} spacing={2}>
                <GenericForm
                  overrideClasses={{
                    selectLabel: { fontSize: 12, fontWeight: 500 },
                  }}
                  fields={values}
                  screenFields={applyI18n(
                    screenFields[mode].slice(0, 1),
                    i18n.language
                  )}
                  setFieldValue={(key, value) => {
                    setFieldValue(key, value);
                    updatePeriod(key, value);
                  }}
                  errors={errors}
                />

                <Grid item xs={12}>
                  <DatePicker
                    id="first_period-hour"
                    keyValue="first_period.hour"
                    type="time"
                    label={i18n.t('START_TIME_KEY')}
                    value={values?.first_period?.hour}
                    setFieldsValue={(key, value) => {
                      if (isValid(value)) {
                        setFieldValue(key, value);
                        updatePeriod(key, format(value as Date, 'HH:mm'));
                      }
                    }}
                    disabled={!values?.first_period?.is_active}
                    onError={error => setHasError(!!error)}
                  />
                </Grid>

                <GenericForm
                  overrideClasses={{
                    selectLabel: { fontSize: 12, fontWeight: 500 },
                  }}
                  fields={values}
                  screenFields={applyI18n(
                    screenFields[mode]
                      .slice(1, mode === 'micro' ? 3 : 4)
                      .map(item => {
                        values.first_period.licences_margin_percent = controlMaxAndMinValues(
                          values?.first_period?.licences_margin_percent
                        ) as number;

                        return {
                          ...item,
                          disabled:
                            !values?.first_period?.is_active ||
                            (item.stateKey ===
                              'first_period.licences_margin_percent' &&
                              values.first_period
                                .scale_instances_by_licences_margin ===
                                'false'),
                        };
                      }),
                    i18n.language
                  )}
                  setFieldValue={(key, value) => {
                    setFieldValue(key, value);
                    updatePeriod(key, value);
                  }}
                  errors={errors}
                />
              </Grid>

              <Grid container item xs={6} spacing={2}>
                <GenericForm
                  overrideClasses={{
                    selectLabel: { fontSize: 12, fontWeight: 500 },
                  }}
                  fields={values}
                  screenFields={applyI18n(
                    mode === 'micro'
                      ? screenFields[mode].slice(3, 4)
                      : screenFields[mode].slice(4, 5),
                    i18n.language
                  )}
                  setFieldValue={(key, value) => {
                    setFieldValue(key, value);
                    updatePeriod(key, value);
                  }}
                  errors={errors}
                />

                <Grid item xs={12}>
                  <DatePicker
                    id="second_period-hour"
                    keyValue="second_period.hour"
                    type="time"
                    label={i18n.t('START_TIME_KEY')}
                    value={values?.second_period?.hour}
                    setFieldsValue={(key, value) => {
                      if (isValid(value)) {
                        setFieldValue(key, value);
                        updatePeriod(key, format(value as Date, 'HH:mm'));
                      }
                    }}
                    disabled={!values?.second_period?.is_active}
                    onError={error => setHasError(!!error)}
                  />
                </Grid>

                <GenericForm
                  overrideClasses={{
                    selectLabel: { fontSize: 12, fontWeight: 500 },
                  }}
                  fields={values}
                  screenFields={applyI18n(
                    screenFields[mode]
                      .slice(mode === 'micro' ? 4 : 5)
                      .map(item => {
                        values.second_period.licences_margin_percent = controlMaxAndMinValues(
                          values?.second_period?.licences_margin_percent
                        ) as number;

                        return {
                          ...item,
                          disabled:
                            !values?.second_period?.is_active ||
                            (item.stateKey ===
                              'second_period.licences_margin_percent' &&
                              values?.second_period
                                ?.scale_instances_by_licences_margin ===
                                'false'),
                        };
                      }),
                    i18n.language
                  )}
                  setFieldValue={(key, value) => {
                    setFieldValue(key, value);
                    updatePeriod(key, value);
                  }}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </ToggleCard>
        )}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {})(PeriodCard);
